import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function USPCreation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('generate'); // generate, generating, review, customizing
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // USP data
  const [uspOptions, setUspOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [customizedUSP, setCustomizedUSP] = useState('');
  const [savedUSP, setSavedUSP] = useState('');
  const [scrapedUSP, setScrapedUSP] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if user has saved USP
    try {
      const response = await fetch('/api/usp/get', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.usp) {
          setSavedUSP(data.usp);
          setCustomizedUSP(data.usp);
          setStage('review');
        }
      }
    } catch (err) {
      // No saved USP, that's fine
    }

    // Load scraped value proposition if available
    try {
      const vpResponse = await fetch('/api/business/field/value_proposition', {
        credentials: 'include'
      });
      if (vpResponse.ok) {
        const vpData = await vpResponse.json();
        if (vpData.field_value) {
          setScrapedUSP(vpData.field_value);
        }
      }
    } catch (err) {
      // No scraped USP, that's fine
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/usp/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUspOptions(data.options || []);
        setStage('review');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate USPs: ${errorData.error || 'Unknown error'}`);
        setStage('generate');
      }
    } catch (err) {
      setError('Error generating USPs: ' + err.message);
      setStage('generate');
    }

    setGenerating(false);
  };

  const handleSelect = (index) => {
    setSelectedIndex(index);
    setCustomizedUSP(uspOptions[index].text);
    setStage('customizing');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    if (!customizedUSP.trim()) {
      setError('Please enter your USP');
      setSaving(false);
      return;
    }

    try {
      const response = await fetch('/api/usp/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          usp: customizedUSP.trim(),
          selected_option: selectedIndex !== null ? uspOptions[selectedIndex] : null
        })
      });

      if (response.ok) {
        setSavedUSP(customizedUSP);
        navigate('/dashboard/business-model');
      } else {
        const errorData = await response.json();
        setError(`Failed to save USP: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving USP: ' + err.message);
    }

    setSaving(false);
  };

  const getAngleBadge = (angle) => {
    const badges = {
      'problem-solution': { bg: 'danger', label: 'Problem/Solution' },
      'transformation': { bg: 'success', label: 'Transformation' },
      'differentiation': { bg: 'primary', label: 'Differentiation' },
      'feature-benefit': { bg: 'info', label: 'Feature/Benefit' },
      'customer-focused': { bg: 'warning', label: 'Customer-Focused' }
    };
    const badge = badges[angle] || { bg: 'secondary', label: angle };
    return <Badge bg={badge.bg}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // Screen 1: Generate or Regenerate
  if (stage === 'generate') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <h2 className="mb-4">Create Your Unique Selling Proposition</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                {scrapedUSP && (
                  <Alert variant="success" className="text-start mb-4">
                    <strong>From your website:</strong>
                    <p className="mb-0 mt-2">"{scrapedUSP}"</p>
                    <small className="text-muted">We'll use this as a starting point and generate variations.</small>
                  </Alert>
                )}

                <Alert variant="info" className="text-start mb-4">
                  <p className="mb-2">
                    <strong>Your USP answers the question:</strong> "Why should someone buy from you instead of your competitors?"
                  </p>
                  <p className="mb-0">
                    I'll analyze everything you've told me about your business, target market, competitors, and products
                    to generate multiple USP options for you to choose from.
                  </p>
                </Alert>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleGenerate}
                  className="px-5"
                >
                  Generate My USP Options →
                </Button>

                <div className="mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 2: Generating (loading state)
  if (stage === 'generating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Analyzing Your Business...</h4>
              <p className="text-muted mt-3">
                I'm reviewing your target market, value proposition, competitive advantages, and product portfolio to craft compelling USP options.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3a: View Saved USP (when returning to review existing)
  if (stage === 'review' && uspOptions.length === 0 && savedUSP) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Your Unique Selling Proposition</h2>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  ✓ Your USP is saved and ready to use in your marketing!
                </Alert>

                <div className="bg-light p-4 rounded mb-4">
                  <p className="mb-0" style={{ fontSize: '1.2rem', fontWeight: '500' }}>
                    "{savedUSP}"
                  </p>
                </div>

                <div className="d-flex gap-3 justify-content-center">
                  <Button
                    variant="outline-primary"
                    onClick={handleGenerate}
                  >
                    Regenerate New Options
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setStage('customizing')}
                  >
                    Edit USP
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3b: Review Options
  if (stage === 'review' && uspOptions.length > 0) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Your USP Options</h2>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  <strong>AI Generated {uspOptions.length} USP Options!</strong> Each approaches your unique value from a different angle.
                  Select the one that resonates most with you, then customize it to make it perfect.
                </Alert>

                <Row>
                  {uspOptions.map((option, index) => (
                    <Col md={12} key={index} className="mb-4">
                      <Card className={`h-100 ${selectedIndex === index ? 'border-primary border-3' : ''}`}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 className="mb-2">Option {index + 1}</h5>
                              {getAngleBadge(option.angle)}
                            </div>
                          </div>

                          <div className="bg-light p-3 rounded mb-3">
                            <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                              "{option.text}"
                            </p>
                          </div>

                          <p className="text-muted small mb-3">
                            <strong>Why this works:</strong> {option.reasoning}
                          </p>

                          <Button
                            variant={selectedIndex === index ? 'success' : 'outline-primary'}
                            onClick={() => handleSelect(index)}
                            className="w-100"
                          >
                            {selectedIndex === index ? '✓ Selected - Customize Below' : 'Select This USP'}
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <div className="text-center mt-3">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('generate')}
                  >
                    ← Regenerate Different Options
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 4: Customize Selected USP
  if (stage === 'customizing') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Customize Your USP</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  Fine-tune the wording to make it perfect for your brand voice. Your USP should be clear, specific, and memorable.
                </Alert>

                <Form.Group className="mb-4">
                  <Form.Label className="h5">Your Unique Selling Proposition</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={customizedUSP}
                    onChange={(e) => setCustomizedUSP(e.target.value)}
                    placeholder="Enter your USP..."
                    style={{ fontSize: '1.1rem' }}
                  />
                  <Form.Text className="text-muted">
                    Character count: {customizedUSP.length} (aim for 50-150 characters for best impact)
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('review')}
                  >
                    ← Back to Options
                  </Button>
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving || !customizedUSP.trim()}
                  >
                    {saving ? 'Saving...' : 'Save My USP →'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default USPCreation;
